import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Row, Col, Form, FloatingLabel, Button, Modal } from "react-bootstrap";
import { ButtonLoader } from "Components";
import { authService } from "Service/Axios";
import {
  FORM_BUILDER_FORM_ADD_URL,
  FORM_BUILDER_FORM_EDIT_URL,
} from "Constant/api";
import { useFolderList, useFormCoverImageList } from "Constant/apiHooks";
import { Validator } from "Utils/validator";
import { uploadHandler } from "Utils";
import { DEFAULT_MODULE_TYPE, IMAGE_TYPES } from "Constant/constants";
import { paths } from "Constant/paths";

export const CreateForm = ({ show, selectedForm, toggle, fetchFolderList }) => {
  const { t } = useTranslation();
  const ref = React.useRef(null);
  const navigate = useNavigate();

  const {
    formBuilder: {
      customizeForm: { root, formDesign },
    },
  } = paths;

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    formName: "",
    folderId: "",
    coverName: "",
    coverImage: "",
  });
  const [errMsg, setErrMsg] = useState({
    formName: "",
    folderId: "",
    coverName: "",
    coverImage: "",
  });

  const { formName, folderId, coverName, coverImage } = formData;

  const {
    coverImageList,
    setCoverImageList,
    loading: coverImageLoading,
  } = useFormCoverImageList();

  const { folderList, loading: folderLoading } = useFolderList();

  useEffect(() => {
    if (selectedForm) {
      setFormData((prev) => ({
        ...prev,
        formName: selectedForm.formName,
        coverName: selectedForm.formCoverName,
        folderId: selectedForm.folderId,
        coverImage: selectedForm.coverImage,
      }));
    }
  }, [selectedForm]);

  useEffect(() => {
    if (selectedForm && selectedForm.coverImage) {
      if (!coverImageList.includes(selectedForm.coverImage)) {
        setCoverImageList([selectedForm.coverImage, ...coverImageList]);
      }
      // const newCoverImageList = [...coverImageList];
    }
  }, [selectedForm, coverImageList]);

  // On Change
  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (e.target.name === "formName") {
      if (!Validator.text(e.target.value)) {
        setErrMsg((prev) => ({
          ...prev,
          formName: t("common.messages.error.common.formName.blank"),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          formName: "",
        }));
      }
    }

    if (e.target.name === "folderId") {
      if (!Validator.text(e.target.value)) {
        setErrMsg((prev) => ({
          ...prev,
          folderId: t("common.messages.error.common.folderId.blank"),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          folderId: "",
        }));
      }
    }

    if (e.target.name === "coverName") {
      if (!Validator.text(e.target.value)) {
        setErrMsg((prev) => ({
          ...prev,
          coverName: t("common.messages.error.common.coverName.blank"),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          coverName: "",
        }));
      }
    }

    if (e.target.name === "coverImage") {
      if (!Validator.text(e.target.value)) {
        setErrMsg((prev) => ({
          ...prev,
          coverImage: t("common.messages.error.common.coverImage.blank"),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          coverImage: "",
        }));
      }
    }
  };

  const handleUploadCoverImage = async (e) => {
    setLoading(true);
    const response = await uploadHandler(
      e,
      DEFAULT_MODULE_TYPE.coverImage,
      IMAGE_TYPES
    );
    if (response && response.url) {
      setCoverImageList([response.url, ...coverImageList]);
      setLoading(false);
    }
  };

  const getValidation = () => {
    return new Promise((resolve) => {
      if (
        Validator.text(formName) &&
        Validator.text(folderId) &&
        Validator.text(coverName) &&
        Validator.text(coverImage)
      ) {
        resolve(true);
      } else {
        if (!Validator.text(formName)) {
          setErrMsg((prev) => ({
            ...prev,
            formName: t("common.messages.error.common.formName.blank"),
          }));
        }
        if (!Validator.text(folderId)) {
          setErrMsg((prev) => ({
            ...prev,
            folderId: t("common.messages.error.common.folderId.blank"),
          }));
        }
        if (!Validator.text(coverName)) {
          setErrMsg((prev) => ({
            ...prev,
            coverName: t("common.messages.error.common.coverName.blank"),
          }));
        }
        if (!Validator.text(coverImage)) {
          setErrMsg((prev) => ({
            ...prev,
            coverImage: t("common.messages.error.common.coverImage.blank"),
          }));
        }
        resolve(false);
      }
    });
  };

  // On Submit
  const onSaveForm = async () => {
    const validationStatus = await getValidation();
    if (validationStatus) {
      setLoading(true);
      try {
        if (selectedForm?.formId) {
          const payload = {
            ...selectedForm,
            formName,
            formCoverName: coverName,
            folderId,
            coverImage,
          };
          const response = await authService(
            FORM_BUILDER_FORM_EDIT_URL,
            "POST",
            payload
          );
          setLoading(false);
          if (response) {
            if (response.success) {
              toast.success(
                t("common.messages.notification.success.updateForm")
              );
              navigate(`${root}/${selectedForm?.formId}/${formDesign}`);
              fetchFolderList();
              setCoverImageList([]);
              toggle();
            } else {
              if (
                response.message ===
                "The form has already exist under this folder."
              ) {
                toast.error(
                  t("common.messages.notification.failed.duplicateForm")
                );
              } else {
                toast.error(response.message);
              }
            }
          }
        } else {
          const payload = {
            formName,
            formCoverName: coverName,
            folderId,
            coverImage,
          };
          const response = await authService(
            FORM_BUILDER_FORM_ADD_URL,
            "POST",
            payload
          );
          setLoading(false);
          if (response) {
            if (response.success) {
              toast.success(
                t("common.messages.notification.success.createForm")
              );
              navigate(`${root}/${response?.formId}/${formDesign}`);
              fetchFolderList();
              setCoverImageList([]);
              toggle();
            } else {
              if (
                response.message ===
                "The form has already exist under this folder."
              ) {
                toast.error(
                  t("common.messages.notification.failed.duplicateForm")
                );
              } else {
                toast.error(response.message);
              }
            }
          }
        }
      } catch (error) {
        setLoading(false);
        if (error.response && error.response.data) {
          toast.error(error.response.data.message);
        }
      }
    }
  };

  const onHide = () => {
    setCoverImageList([]);
    toggle();
  };

  return (
    <Modal
      show={show}
      onHide={toggle}
      size="lg"
      centered
      backdrop="static"
      keyboard={false}
      ref={ref}
    >
      <Modal.Header className="justify-content-center">
        <Modal.Title>{t("auth.formBuilder.createForm")}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-4">
        <Row>
          <Col lg={6} md={6} sm={12} xs={12}>
            <FloatingLabel
              controlId="floatingInput"
              label={t("common.label.formName")}
              className="mb-3 grey-filed"
            >
              <Form.Control
                type="text"
                placeholder={t("common.placeHolder.formName")}
                name="formName"
                value={formName}
                onChange={handleChange}
              />
              {errMsg.formName && <p className="error">{errMsg.formName}</p>}
            </FloatingLabel>
          </Col>
          <Col lg={6} md={6} sm={12} xs={12}>
            <FloatingLabel
              controlId="floatingSelect"
              label={t("common.label.folder")}
              className="mb-3 grey-filed"
            >
              <Form.Select
                aria-label="Floating label select example"
                name="folderId"
                value={folderId}
                onChange={handleChange}
              >
                <option value="">Select</option>
                {!folderLoading &&
                  folderList &&
                  folderList.length > 0 &&
                  folderList.map((folder) => (
                    <option value={folder.gid}>{folder.name}</option>
                  ))}
              </Form.Select>
              {errMsg.folderId && <p className="error">{errMsg.folderId}</p>}
            </FloatingLabel>
          </Col>
          <Col lg={6} md={6} sm={12} xs={12}>
            <FloatingLabel
              controlId="floatingInput"
              label={t("common.label.coverName")}
              className="mb-3 grey-filed"
            >
              <Form.Control
                type="text"
                placeholder={t("common.placeHolder.coverName")}
                name="coverName"
                value={coverName}
                onChange={handleChange}
              />
              {errMsg.coverName && <p className="error">{errMsg.coverName}</p>}
            </FloatingLabel>
          </Col>
        </Row>

        <div className="modal-title h4 mb-4">
          {t("common.label.chooseCoverImage")}
        </div>
        <div className="cover-image-list">
          <Row className="gy-4">
            <Col lg={4} md={6} sm={12} xs={12}>
              <div className="file-input-box bg-white border rounded height-equal">
                <label
                  htmlFor="file"
                  className="d-block p-2 text-center w-full height-equal d-flex flex-column justify-content-center align-items-center"
                >
                  <span className="material-symbols-outlined align-middle mb-2">
                    {t("common.label.upload")}
                  </span>
                  <h5 className="mb-0">{t("common.label.uploadYourImage")}</h5>
                  {/* <small>File Size 20 MB</small> */}
                </label>
                <input
                  type="file"
                  id="file"
                  className="d-none"
                  onChange={handleUploadCoverImage}
                />
              </div>
            </Col>
            {coverImageLoading ? (
              <h1>Image Loading</h1>
            ) : (
              coverImageList &&
              coverImageList.length > 0 &&
              coverImageList.map((cvrImg, index) => (
                <Col lg={4} md={6} sm={12} xs={12} key={`${index.toString()}`}>
                  <div className="cover-form-box p-2 rounded border relative">
                    <Form.Check
                      className="form-check-success"
                      type="radio"
                      id={`default-${index.toString()}`}
                      name="coverImage"
                      label={coverName}
                      value={cvrImg}
                      checked={cvrImg === coverImage}
                      onChange={handleChange}
                    />
                    <img src={cvrImg} alt={`coverImage${index + 1}`} />
                  </div>
                </Col>
              ))
            )}
          </Row>
          {errMsg.coverImage && <p className="error">{errMsg.coverImage}</p>}
        </div>
      </Modal.Body>
      <Modal.Footer className="border-0 justify-content-center pb-4">
        <Button variant="outline-primary" onClick={onHide}>
          {t("common.button.cancel")}
        </Button>
        {!loading ? (
          <Button variant="primary" onClick={onSaveForm}>
            {selectedForm?.formId
              ? t("common.button.update")
              : t("common.button.create")}{" "}
            {t("common.button.form")}
          </Button>
        ) : (
          <ButtonLoader />
        )}
      </Modal.Footer>
    </Modal>
  );
};
